import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Typography } from '@mui/material';
import cx from 'classnames';
import parse from 'html-react-parser';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { BREAKPOINTS_VALUES, MIDDLE_MARGIN, SMALL_MARGIN, useIsWidthUp } from '../../theme';
import { WeAreBlock as WeAreBlockType } from '../../types';
import { parseHighlightedText } from '../../utils/helpers';
import { useEventListener } from '../../utils/hooks';
import useIsSsr from '../../utils/hooks/useIsSsr';
import useTextStyles from '../../utils/textStyles';
import { InfoItem } from './InfoItem';
import { SliderCard } from './SliderCard';
import useStyles from './styles';

const swiperSettings: SwiperOptions = {
  slidesPerView: 1.15,
  spaceBetween: SMALL_MARGIN,
  observer: true,
  pagination: {
    clickable: true,
  },
  breakpoints: {
    [BREAKPOINTS_VALUES.lg]: {
      slidesPerView: 1.5,
      spaceBetween: MIDDLE_MARGIN,
    },
  },
};

interface WeAreBlockProps {
  data?: WeAreBlockType;
}

const WeAreBlock: FC<WeAreBlockProps> = ({ data }) => {
  const classes = useStyles(data?.background)();
  const textClasses = useTextStyles();

  const isSsr = useIsSsr();
  const isMobile = !useIsWidthUp('md');
  const isLg = useIsWidthUp('lg');

  const swiperWrapperRef = useRef<HTMLDivElement>(null);

  const [lastSlideOffset, setLastSlideOffset] = useState(0);

  const title = useMemo(
    () =>
      data?.heading
        ? parse(
            parseHighlightedText(
              data?.heading,
              (match) => `<span>${match.slice(2, -2)}</span><br />`,
            ),
          )
        : '',
    [data],
  );

  const settings = useMemo(() => {
    return {
      ...swiperSettings,
      slidesOffsetAfter: lastSlideOffset,
    };
  }, [lastSlideOffset]);

  const getLastSlideOffset = useCallback(() => {
    const width = swiperWrapperRef?.current?.clientWidth;
    let offset = 0;

    if (width) {
      offset = isLg ? (width / 1.5) * 0.5 : (width / 1.15) * 0.15;
    }

    setLastSlideOffset(offset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiperWrapperRef.current, isLg]);

  useEventListener('resize', getLastSlideOffset);

  useEffect(() => {
    getLastSlideOffset();
  }, [getLastSlideOffset]);

  return (
    <div
      className={cx(classes.wrapper, data?.background === 'Dark' ? 'dark-block' : 'light-block')}
    >
      {isMobile && (
        <div className={classes.textWrapper}>
          <Typography variant="h2" className={cx(textClasses.h2, classes.header)}>
            {title}
          </Typography>
        </div>
      )}
      <div className={classes.container}>
        <div className={classes.col}>
          <div className={classes.textWrapper}>
            {!isMobile && (
              <Typography variant="h2" className={cx(textClasses.h2, classes.header)}>
                {title}
              </Typography>
            )}
            {Boolean(data?.items?.length) && !isSsr && (
              <div>
                {data?.items?.map((item) => (
                  <InfoItem
                    key={item?.icon}
                    iconUrl={item?.icon}
                    title={item?.title}
                    description={item?.description}
                    wrapperClass={classes.infoItem}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        {Boolean(data?.sliders?.length && data?.sliders?.length > 1) && (
          <div className={cx(classes.col, classes.rightColumn)} ref={swiperWrapperRef}>
            <Swiper {...settings}>
              {data?.sliders?.map((item) => (
                <SwiperSlide key={item?.image}>
                  <div>
                    <SliderCard imageUrl={item?.image} labelText={item?.label} />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
        {Boolean(data?.sliders?.length && data.sliders.length === 1 && data.sliders[0]) && (
          <div className={cx(classes.col, classes.rightColumn)}>
            <div className={classes.singleCardWrapper}>
              {/*@ts-ignore*/}
              <SliderCard imageUrl={data.sliders[0]?.image} labelText={data.sliders[0]?.label} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeAreBlock;
